import { z } from 'zod';

import { i18n } from '@/lib/i18n';

const MIN_REQUIRED_FIELD_VALUE_LENGTH = 1;

const getRequiredStringFiledSchema = (field: string) =>
  z.string().min(MIN_REQUIRED_FIELD_VALUE_LENGTH, i18n.t('required-field', { field }));

const requiredAddressFields = [
  ['address', i18n.t('address')],
  ['houseNumber', i18n.t('houseNumber')],
  ['street', i18n.t('street')],
  ['postalCode', i18n.t('postalCode')],
  ['city', i18n.t('city')],
  ['countryId', i18n.t('country')],
] as const;

type CreateEstateSchemaOptions = {
  isAddressRequired?: boolean;
};

export const getCreateEstateSchema = ({
  isAddressRequired = true,
}: CreateEstateSchemaOptions = {}) =>
  z
    .object({
      estateData: z.object({
        name: getRequiredStringFiledSchema(i18n.t('name')),
        marketType: z.string(),
        propertyType: z.string(),
        propertyLevel: getRequiredStringFiledSchema(i18n.t('propertyLevel')),
        tenantId: getRequiredStringFiledSchema(i18n.t('tenant')),
        projectId: z.string().optional(),
        creatorId: z.string().optional(),
        availability: z.enum(['available', 'reserved', 'sold', 'rented']).nullable().optional(),
      }),
      addressData: z.object({
        addressAutoComplete: z.string().optional(),
        address: z.string(),
        houseNumber: z.string(),
        street: z.string(),
        postalCode: z.string(),
        city: z.string(),
        countryId: z.string(),
        lat: z.number().optional(),
        long: z.number().optional(),
      }),
    })
    .superRefine((data, ctx) => {
      const shouldRequireAddress = isAddressRequired || !data.estateData.projectId;

      if (!shouldRequireAddress) return;

      requiredAddressFields.forEach(([field, label]) => {
        if (!data.addressData[field]?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('required-field', { field: label }),
            path: ['addressData', field],
          });
        }
      });
    });

export type EstateSchemaType = z.infer<ReturnType<typeof getCreateEstateSchema>>;
